import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, Loader2, ArrowRight, Mail } from "lucide-react";

export default function SubscribeSuccessPage() {
  const navigate = useNavigate();
  const { setClientFromExternal } = useAuth();

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [activated, setActivated] = useState(false);
  const [message, setMessage] = useState("");

  const tryLookup = async () => {
    if (!email.trim()) {
      toast.error("Enter the email you used to pay");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const r = await axios.post(`${API}/payments/lookup`, { email: email.trim() });
      if (r.data?.active) {
        localStorage.setItem("abpt_token", r.data.token);
        setClientFromExternal?.(r.data.client);
        setActivated(true);
        toast.success("Subscription active!");
      } else {
        setMessage(r.data?.message || "Not active yet — try again in a few seconds.");
      }
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F2]">
      <Header />
      <main className="flex-1 py-12 sm:py-20">
        <div className="max-w-lg mx-auto px-4">
          <div className="border border-[#E7E3D7] bg-white p-6 sm:p-9 text-center">
            {activated ? (
              <>
                <CheckCircle2 className="mx-auto h-14 w-14 text-[#0F6B3B]" />
                <h1 className="mt-6 font-display text-[1.75rem] sm:text-3xl font-bold tracking-[-0.02em] text-[#14211B]">
                  You're in
                </h1>
                <p className="mt-3 text-sm text-[#56655C]">
                  Your APT subscription is active. Time to prep.
                </p>
                <Button
                  className="mt-8 h-12 w-full rounded-md bg-[#0F6B3B] text-sm font-semibold text-white hover:bg-[#0B5730]"
                  onClick={() => navigate("/dashboard")}
                  data-testid="goto-dashboard-btn"
                >
                  Go to my plan <ArrowRight className="ml-3 h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Mail className="mx-auto h-12 w-12 text-[#0F6B3B]" />
                <h1 className="mt-6 font-display text-[1.75rem] sm:text-3xl font-bold tracking-[-0.02em] text-[#14211B]">
                  Activate your access
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-[#56655C]">
                  Enter the email you used at Stripe checkout. We'll confirm your subscription and log
                  you in.
                </p>
                <div className="mt-7 text-left">
                  <Label
                    htmlFor="email"
                    className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6A776F]"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-12 border-[#DDE3D5] bg-white text-base text-[#14211B]"
                    data-testid="activate-email-input"
                  />
                </div>
                <Button
                  className="mt-6 h-12 w-full rounded-md bg-[#0F6B3B] text-sm font-semibold text-white hover:bg-[#0B5730]"
                  onClick={tryLookup}
                  disabled={busy}
                  data-testid="activate-btn"
                >
                  {busy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…
                    </>
                  ) : (
                    "Activate my subscription"
                  )}
                </Button>
                {message && (
                  <p
                    className="mt-5 border border-[#E3C98A] bg-[#FBF3DE] p-3 text-sm text-[#8A6A1F]"
                    data-testid="activate-message"
                  >
                    {message}
                  </p>
                )}
                <p className="mt-6 text-xs leading-relaxed text-[#8A968E]">
                  Just paid and not seeing it yet? Stripe sometimes takes a few seconds. Wait 10
                  seconds and try again.
                </p>
                <div className="mt-4 text-xs text-[#8A968E]">
                  Need help?{" "}
                  <Link to="/" className="underline hover:text-[#0F6B3B]">
                    Back to home
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
